/**
 * DocumentsView - Unified Document Management View
 * Five-level accordion per docstore.yaml and ACCORDION_UI_PATTERN.md
 *
 * Level 1: Scope (VEHICLE, DRIVER, CLAIM, CONTRACT, ADMIN) - exclusive
 * Level 2: Status (ACTIFS / ARCHIVES) - exclusive toggle per RGPD
 * Level 3: Entity (VHC_000001, DRV_000001, etc.) - non-exclusive
 * Level 4: Folder (10_REGISTRATION, 30_INSURANCE, etc.) - non-exclusive
 * Level 5: Documents
 *
 * Structure mirrors filesystem:
 * FMM_DATA/TENANTS/<TENANT>/VEHICLES/<VHC_ID>/10_REGISTRATION/document.pdf
 *
 * RGPD Archive Criteria:
 * - Document expired (valid_to < today)
 * - Status = 'archived' or 'inactive'
 * - marked_for_deletion = true
 * - is_superseded = true (old version)
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText, Search, Upload, Loader2, FolderOpen, Folder,
  Car, Users, Shield, Briefcase, ChevronDown, ChevronRight,
  RefreshCw, AlertTriangle, Clock,
  Image, File, Archive, FileWarning, HardDrive, Database,
  CheckCircle, ArchiveRestore, User, XCircle, Minus, Slash,
  TrendingUp, Eye, ClipboardCheck
} from 'lucide-react';
import api, { documentAPI } from '../../api.js';
import DocumentDropZone from '../common/DocumentDropZone.jsx';
import DocumentValidationModal from './DocumentValidationModal.jsx';
import DocumentRejectionModal from './DocumentRejectionModal.jsx';
import CannotProvideModal from './CannotProvideModal.jsx';
import DocumentViewerModal from '../common/DocumentViewerModal.jsx';
import DocumentItem from './DocumentItem.jsx';
import {
  SCOPE_CONFIG,
  FOLDER_STRUCTURE,
  getVisibleFolders,
  getFolder,
  getDocumentTypeLabel,
  getArchiveStatus
} from '../../config/docstore.js';
import { DocumentComplianceEngine, FOLDER_STATUS } from '../../lib/DocumentComplianceEngine.js';
import { getFleetManagerConfig } from '../../config/complianceRules.js';
import { useSearch } from '../../v3/context/SearchContext.jsx';
import { useFormat, formatDate } from '../../i18n/format.js';

// Icon map for dynamic rendering
const ICON_MAP = {
  Car, Users, Shield, Briefcase, AlertTriangle
};

// Status icon and color map for compliance badges
const STATUS_STYLE_MAP = {
  [FOLDER_STATUS.COMPLETE]: {
    icon: CheckCircle,
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
  },
  [FOLDER_STATUS.MISSING_REQUIRED]: {
    icon: XCircle,
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
  },
  [FOLDER_STATUS.EXPIRING_SOON]: {
    icon: Clock,
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200',
  },
  [FOLDER_STATUS.EXPIRED]: {
    icon: XCircle,
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
  },
  [FOLDER_STATUS.OPTIONAL_EMPTY]: {
    icon: Minus,
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-400',
    borderColor: 'border-gray-100',
  },
  [FOLDER_STATUS.HAS_DOCUMENTS]: {
    icon: FileText,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-100',
  },
  [FOLDER_STATUS.NOT_APPLICABLE]: {
    icon: Slash,
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-300',
    borderColor: 'border-gray-100',
  },
  // Validation workflow states
  [FOLDER_STATUS.PENDING_VALIDATION]: {
    icon: Clock,
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
  },
  [FOLDER_STATUS.VALIDATION_REJECTED]: {
    icon: XCircle,
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
  },
  [FOLDER_STATUS.EXCEPTION_RECORDED]: {
    icon: AlertTriangle,
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-200',
  }
};

// Initialize compliance engine with FleetManager rules
const complianceEngine = new DocumentComplianceEngine(getFleetManagerConfig());

// Get file icon based on MIME type - used by child components
// eslint-disable-next-line no-unused-vars
function _getFileIcon(mimeType, className = 'w-5 h-5') {
  if (mimeType?.startsWith('image/')) {
    return <Image className={`${className} text-green-600`} />;
  }
  if (mimeType === 'application/pdf') {
    return <FileText className={`${className} text-red-600`} />;
  }
  return <File className={`${className} text-blue-600`} />;
}

// Calculate expiry status
function getExpiryStatus(validTo) {
  if (!validTo) return null;
  const today = new Date();
  const expiry = new Date(validTo);
  const daysUntil = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

  if (daysUntil < 0) return { status: 'expired', days: Math.abs(daysUntil) };
  if (daysUntil <= 30) return { status: 'critical', days: daysUntil };
  if (daysUntil <= 90) return { status: 'warning', days: daysUntil };
  return { status: 'ok', days: daysUntil };
}

// Format date - used by child components
// eslint-disable-next-line no-unused-vars
function _formatDate(dateStr) {
  if (!dateStr) return '-';
  return formatDate(dateStr);
}

// Format file size
function formatSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DocumentsView({ onMessage }) {
  const { t } = useFormat();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { searchQuery } = useSearch();
  const [showUpload, setShowUpload] = useState(false);

  // Entity data from database
  const [entities, setEntities] = useState({
    vehicles: [],
    drivers: [],
    claims: [],
    contracts: []
  });

  // Accordion state - 5 levels
  const [expandedScope, setExpandedScope] = useState(null);         // Level 1 - exclusive
  const [expandedStatus, setExpandedStatus] = useState({});         // Level 2 - exclusive per scope ('active' or 'archived')
  const [expandedEntities, setExpandedEntities] = useState(new Set()); // Level 3 - non-exclusive
  const [expandedFolders, setExpandedFolders] = useState(new Set());  // Level 4 - non-exclusive

  const [deletingId, setDeletingId] = useState(null);

  // Validation modal state
  const [validationModal, setValidationModal] = useState({ isOpen: false, document: null, entityData: {} });
  const [rejectionModal, setRejectionModal] = useState({ isOpen: false, document: null });
  const [cannotProvideModal, setCannotProvideModal] = useState({ isOpen: false, document: null });
  const [viewerDocument, setViewerDocument] = useState(null);

  // Upload state
  const [uploadTarget, setUploadTarget] = useState({ scope: null, entityId: null, folderCode: null });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  // Load all data (documents + entities)
  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch documents and entities in parallel
      const [docsResult, vehiclesResult, driversResult, claimsResult, contractsResult] = await Promise.all([
        documentAPI.getAll({}),
        api.vehicle.getAll(),
        api.driver.getAll(),
        api.claim.getAll(),
        api.insurance_contract.getAll()
      ]);

      if (docsResult.ok) {
        setDocuments(docsResult.data || []);
      }

      setEntities({
        vehicles: vehiclesResult.ok ? (vehiclesResult.data || []) : [],
        drivers: driversResult.ok ? (driversResult.data || []) : [],
        claims: claimsResult.ok ? (claimsResult.data || []) : [],
        contracts: contractsResult.ok ? (contractsResult.data || []) : []
      });
    } catch (err) {
      console.error('Failed to load data:', err);
      onMessage?.({
        title: t('documents.messages.errorTitle'),
        message: t('documents.messages.loadFailed'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Helper to generate reference ID from numeric ID
  const makeRefId = (prefix, id) => `${prefix}_${String(id).padStart(6, '0')}`;

  // Create entity label maps for human-readable display
  // Includes full entity data for compliance conditional checks
  const entityMaps = useMemo(() => {
    const vehicleMap = {};
    const driverMap = {};
    const claimMap = {};
    const contractMap = {};

    // Vehicles: "AB-123-CD (Renault Clio)"
    entities.vehicles.forEach(v => {
      const refId = makeRefId('VHC', v.id);
      vehicleMap[refId] = {
        id: v.id,
        refId,
        label: v.license_plate || refId,
        sublabel: `${v.make || ''} ${v.model || ''}`.trim() || null,
        status: v.status,
        icon: Car,
        // Full entity data for conditional rule evaluation
        data: {
          acquisition_type: v.acquisition_type,
          status: v.status,
          ...v
        }
      };
    });

    // Drivers: "Jean DUPONT"
    entities.drivers.forEach(d => {
      const refId = makeRefId('DRV', d.id);
      driverMap[refId] = {
        id: d.id,
        refId,
        label: `${d.first_name || ''} ${(d.last_name || '').toUpperCase()}`.trim() || refId,
        sublabel: d.license_number || null,
        status: d.status,
        icon: User,
        data: {
          requires_certification: d.requires_certification || d.driver_type === 'professional',
          status: d.status,
          ...d
        }
      };
    });

    // Claims: "SIN-2024-001 (Collision)"
    entities.claims.forEach(c => {
      const refId = makeRefId('CLM', c.id);
      claimMap[refId] = {
        id: c.id,
        refId,
        label: c.claim_number || refId,
        sublabel: c.type ? c.type.charAt(0).toUpperCase() + c.type.slice(1) : null,
        status: c.status,
        icon: AlertTriangle,
        data: {
          requires_expert: c.requires_expert || c.damage_amount > 5000,
          status: c.status,
          ...c
        }
      };
    });

    // Contracts: "POL-2024-001 (AXA)"
    entities.contracts.forEach(c => {
      const refId = makeRefId('CNT', c.id);
      contractMap[refId] = {
        id: c.id,
        refId,
        label: c.policy_number || refId,
        sublabel: c.insurer_name || null,
        status: c.status,
        icon: Shield,
        data: {
          status: c.status,
          ...c
        }
      };
    });

    return {
      VEHICLE: vehicleMap,
      DRIVER: driverMap,
      CLAIM: claimMap,
      CONTRACT: contractMap,
      ADMIN: {} // Admin has no entities
    };
  }, [entities]);

  // Evaluate compliance for all entities
  const complianceReports = useMemo(() => {
    const reports = {};

    // Build entity arrays with id and data for each scope
    const scopeEntities = {
      VEHICLE: Object.values(entityMaps.VEHICLE).map(e => ({ id: e.refId, data: e.data })),
      DRIVER: Object.values(entityMaps.DRIVER).map(e => ({ id: e.refId, data: e.data })),
      CLAIM: Object.values(entityMaps.CLAIM).map(e => ({ id: e.refId, data: e.data })),
      CONTRACT: Object.values(entityMaps.CONTRACT).map(e => ({ id: e.refId, data: e.data }))
    };

    // Evaluate each scope
    Object.entries(scopeEntities).forEach(([scope, entityList]) => {
      if (entityList.length > 0) {
        const entityReports = complianceEngine.evaluateEntities(scope, entityList, documents);
        reports[scope] = {};
        entityReports.forEach(report => {
          reports[scope][report.entityId] = report;
        });
      } else {
        reports[scope] = {};
      }
    });

    // Generate summaries per scope
    const summaries = {};
    Object.entries(reports).forEach(([scope, entityReports]) => {
      const reportsList = Object.values(entityReports);
      if (reportsList.length > 0) {
        summaries[scope] = complianceEngine.generateSummary(reportsList);
      } else {
        summaries[scope] = { totalEntities: 0, completeEntities: 0, averageCompletenessPercent: 0 };
      }
    });

    return { reports, summaries };
  }, [entityMaps, documents]);

  // Get all entity IDs for a scope (from database)
  const getAllEntitiesForScope = (scope) => {
    switch (scope) {
      case 'VEHICLE':
        return entities.vehicles.map(v => makeRefId('VHC', v.id));
      case 'DRIVER':
        return entities.drivers.map(d => makeRefId('DRV', d.id));
      case 'CLAIM':
        return entities.claims.map(c => makeRefId('CLM', c.id));
      case 'CONTRACT':
        return entities.contracts.map(c => makeRefId('CNT', c.id));
      default:
        return [];
    }
  };

  // Group documents: scope → archive status → entity → folder → documents
  const groupedData = useMemo(() => {
    const byScope = {};
    let totalCount = 0;
    let totalExpiring = 0;
    let totalSize = 0;
    let totalActive = 0;
    let totalArchived = 0;

    // Count entities from database
    const entityCounts = {
      VEHICLE: entities.vehicles.length,
      DRIVER: entities.drivers.length,
      CLAIM: entities.claims.length,
      CONTRACT: entities.contracts.length,
      ADMIN: 0
    };

    // Initialize all scopes
    Object.keys(SCOPE_CONFIG).forEach(scope => {
      const folders = getVisibleFolders(scope);
      byScope[scope] = {
        config: SCOPE_CONFIG[scope],
        active: {
          entities: {},
          totalCount: 0,
          totalSize: 0,
          warningCount: 0
        },
        archived: {
          entities: {},
          totalCount: 0,
          totalSize: 0
        },
        folders, // Reference for folder configs
        totalCount: 0,
        expiringCount: 0,
        expiredCount: 0,
        totalSize: 0,
        entityCount: entityCounts[scope] || 0 // Total entities from DB
      };

      // Pre-populate all entities from database (in active section by default)
      const allEntityIds = getAllEntitiesForScope(scope);
      allEntityIds.forEach(entityId => {
        byScope[scope].active.entities[entityId] = {
          id: entityId,
          folders: {},
          totalCount: 0,
          totalSize: 0,
          warningCount: 0
        };
      });
    });

    // Filter by search query
    let filteredDocs = documents;
    if (searchQuery && searchQuery.length >= 2) {
      const query = searchQuery.toLowerCase();
      filteredDocs = documents.filter(doc =>
        doc.filename?.toLowerCase().includes(query) ||
        doc.original_filename?.toLowerCase().includes(query) ||
        doc.reference_id?.toLowerCase().includes(query) ||
        doc.document_type?.toLowerCase().includes(query)
      );
    }

    // Group documents by scope → status → entity → folder
    filteredDocs.forEach(doc => {
      const scope = doc.scope || 'ADMIN';
      const entityId = doc.reference_id || 'UNKNOWN';
      const folderCode = getFolder(scope, doc.document_type);
      const { isArchived, reason } = getArchiveStatus(doc);

      if (!byScope[scope]) return;

      // Determine target (active or archived)
      const target = isArchived ? byScope[scope].archived : byScope[scope].active;

      // Initialize entity if not exists
      if (!target.entities[entityId]) {
        target.entities[entityId] = {
          id: entityId,
          folders: {},
          totalCount: 0,
          totalSize: 0,
          warningCount: 0
        };
      }

      // Initialize folder for entity if not exists
      if (!target.entities[entityId].folders[folderCode]) {
        const folderConfig = byScope[scope].folders.find(f => f.code === folderCode) || {
          code: folderCode,
          label: folderCode,
          color: 'bg-gray-100 text-gray-700'
        };
        target.entities[entityId].folders[folderCode] = {
          config: folderConfig,
          docs: [],
          totalSize: 0
        };
      }

      // Add document
      const docWithArchiveInfo = { ...doc, _archiveReason: reason };
      target.entities[entityId].folders[folderCode].docs.push(docWithArchiveInfo);
      target.entities[entityId].folders[folderCode].totalSize += doc.file_size || 0;
      target.entities[entityId].totalCount++;
      target.entities[entityId].totalSize += doc.file_size || 0;
      target.totalCount++;
      target.totalSize += doc.file_size || 0;
      byScope[scope].totalCount++;
      byScope[scope].totalSize += doc.file_size || 0;
      totalCount++;
      totalSize += doc.file_size || 0;

      if (isArchived) {
        totalArchived++;
      } else {
        totalActive++;
        // Check for warnings on active documents
        const exp = getExpiryStatus(doc.valid_to);
        if (exp && (exp.status === 'critical' || exp.status === 'warning')) {
          target.entities[entityId].warningCount++;
          target.warningCount++;
          byScope[scope].expiringCount++;
          totalExpiring++;
        }
      }
    });

    return { byScope, totalCount, totalExpiring, totalSize, totalActive, totalArchived };
  }, [documents, searchQuery, entities]);

  // Toggle scope (exclusive - only one open)
  const toggleScope = (scope) => {
    if (expandedScope === scope) {
      setExpandedScope(null);
    } else {
      setExpandedScope(scope);
      // Default to showing active documents
      if (!expandedStatus[scope]) {
        setExpandedStatus(prev => ({ ...prev, [scope]: 'active' }));
      }
    }
  };

  // Toggle status (exclusive per scope - active or archived)
  const toggleStatus = (scope, status) => {
    setExpandedStatus(prev => ({
      ...prev,
      [scope]: prev[scope] === status ? null : status
    }));
  };

  // Toggle entity (non-exclusive - multiple can be open)
  const toggleEntity = (entityKey) => {
    const newExpanded = new Set(expandedEntities);
    if (newExpanded.has(entityKey)) {
      newExpanded.delete(entityKey);
    } else {
      newExpanded.add(entityKey);
    }
    setExpandedEntities(newExpanded);
  };

  // Toggle folder (non-exclusive - multiple can be open)
  const toggleFolder = (folderKey) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderKey)) {
      newExpanded.delete(folderKey);
    } else {
      newExpanded.add(folderKey);
    }
    setExpandedFolders(newExpanded);
  };

  // Handle download - called from DocumentItem component
  const handleDownload = async (doc) => {
    try {
      await documentAPI.download(doc.id, doc.original_filename || doc.filename);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  // Handle view document in modal - called from DocumentItem component
  const handleView = (doc) => {
    setViewerDocument(doc);
  };

  // Handle validate - opens validation modal for a document
  const handleValidate = (doc) => {
    // Get entity data for this document
    let entityData = {};
    if (doc.scope === 'VEHICLE') {
      entityData = entities.vehicles.find(v => v.id === doc.reference_id) || {};
    } else if (doc.scope === 'DRIVER') {
      entityData = entities.drivers.find(d => d.id === doc.reference_id) || {};
    }

    setValidationModal({
      isOpen: true,
      document: doc,
      entityData
    });
  };

  // Handle delete - called from DocumentItem component
  const handleDelete = async (doc) => {
    if (!window.confirm(t('documents.confirm.delete', { name: doc.original_filename || doc.filename }))) return;

    setDeletingId(doc.id);
    try {
      const result = await documentAPI.delete(doc.id, 'User deletion');
      if (result.ok) {
        loadData();
        onMessage?.({ title: t('documents.messages.successTitle'), message: t('documents.messages.deleted'), type: 'success' });
      }
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeletingId(null);
    }
  };

  // Handle upload complete
  const handleUploadComplete = (docs) => {
    setShowUpload(false);
    loadData();
    onMessage?.({
      title: t('documents.messages.successTitle'),
      message: t('documents.messages.uploadedCount', { count: docs.length }),
      type: 'success'
    });
  };

  // ============================================
  // Validation Handlers
  // ============================================

  const handleValidateClick = (folderCode, folderDocs) => {
    if (!folderDocs || folderDocs.length === 0) return;

    // Get the most recent document that needs validation
    const pendingDoc = folderDocs.find(d => d.validation_status === 'pending') || folderDocs[0];

    // Get entity data for this document
    let entityData = {};
    if (pendingDoc.scope === 'VEHICLE') {
      entityData = entities.vehicles.find(v => v.id === pendingDoc.reference_id) || {};
    } else if (pendingDoc.scope === 'DRIVER') {
      entityData = entities.drivers.find(d => d.id === pendingDoc.reference_id) || {};
    }

    setValidationModal({
      isOpen: true,
      document: pendingDoc,
      entityData
    });
  };

  const handleValidationCompleted = (result) => {
    loadData(); // Reload documents
    onMessage?.({ type: 'success', content: t('documents.messages.validated') });
  };

  const handleRejectionCompleted = (result) => {
    loadData(); // Reload documents
    onMessage?.({ type: 'success', content: t('documents.messages.rejected') });
  };

  const handleCannotProvideCompleted = (result) => {
    loadData(); // Reload documents
    onMessage?.({ type: 'success', content: t('documents.messages.exceptionRecorded') });
  };

  // Reserved for future batch request feature
  // eslint-disable-next-line no-unused-vars
  const _handleRequestClick = (_folderCode, _isMissing) => {
    // Open batch request modal or generate .eml
    // For now, show a simple alert
    onMessage?.({
      type: 'info',
      content: t('documents.messages.batchRequestWip')
    });
  };

  // Reserved for future document preview feature
  // eslint-disable-next-line no-unused-vars
  const _handleViewDocumentClick = (_folderCode, folderDocs) => {
    if (folderDocs.length > 0) {
      const doc = folderDocs[0];
      const url = documentAPI.getDownloadUrl(doc.id);
      window.open(url, '_blank');
    }
  };

  // Upload Handlers
  // ============================================

  const handleUploadClick = (scope, entityId, folderCode) => {
    setUploadTarget({ scope, entityId, folderCode });
    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Helper to get the first document type for a folder
  const getDocumentTypeForFolder = (scope, folderCode) => {
    const folders = FOLDER_STRUCTURE[scope] || [];
    const folder = folders.find(f => f.code === folderCode);
    if (folder && folder.documentTypes && folder.documentTypes.length > 0) {
      return folder.documentTypes[0]; // Use first document type for folder
    }
    // Fallback to scope + MISC
    return `${scope}_MISC`;
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !uploadTarget.scope || !uploadTarget.entityId) {
      return;
    }

    // Get the correct document type for this folder
    const documentType = getDocumentTypeForFolder(uploadTarget.scope, uploadTarget.folderCode);

    setUploading(true);
    try {
      const result = await documentAPI.upload(file, {
        scope: uploadTarget.scope,
        reference_id: uploadTarget.entityId,
        document_type: documentType,
        folder_code: uploadTarget.folderCode // Also pass folder code for correct placement
      });

      if (result.ok || result.id) {
        onMessage?.({ type: 'success', content: t('documents.messages.uploaded', { name: file.name }) });
        loadData(); // Reload documents
      } else {
        onMessage?.({ type: 'error', content: result.error?.message || t('documents.messages.uploadFailed') });
      }
    } catch (err) {
      console.error('Upload error:', err);
      onMessage?.({ type: 'error', content: t('documents.messages.errorPrefix', { message: err.message }) });
    } finally {
      setUploading(false);
      setUploadTarget({ scope: null, entityId: null, folderCode: null });
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Database className="w-6 h-6 text-blue-600" />
              {t('documents.header.title')}
            </h2>
            <p className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                {t('documents.header.activeCount', { count: groupedData.totalActive })}
              </span>
              <span className="flex items-center gap-1 text-gray-500">
                <Archive className="w-4 h-4" />
                {t('documents.header.archivedCount', { count: groupedData.totalArchived })}
              </span>
              <span className="text-gray-400">|</span>
              <span>{formatSize(groupedData.totalSize)}</span>
              {groupedData.totalExpiring > 0 && (
                <span className="text-orange-600 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  {t('documents.header.toRenewCount', { count: groupedData.totalExpiring })}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Upload className="w-4 h-4" />
            {t('documents.header.addDocuments')}
          </button>
        </div>

        {/* Refresh */}
        <div className="flex gap-4 justify-end">
          <button
            onClick={loadData}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
            title={t('common.actions.refresh')}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Upload Zone */}
      {showUpload && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('documents.header.addDocuments')}</h3>
          <DocumentDropZone
            scope="ADMIN"
            referenceId="GLOBAL"
            maxFiles={20}
            maxSizeBytes={50 * 1024 * 1024}
            onUploadComplete={handleUploadComplete}
            onError={(error) => onMessage?.({
              title: t('documents.messages.errorTitle'),
              message: error.message || t('documents.messages.uploadFailedShort'),
              type: 'error'
            })}
          />
          <p className="text-sm text-blue-700 mt-3">
            {t('documents.header.uploadHint')}
          </p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      )}

      {/* Five-level Accordion */}
      {!loading && (
        <div className="space-y-3">
          {Object.entries(SCOPE_CONFIG).map(([scope, config]) => {
            const scopeData = groupedData.byScope[scope];
            if (!scopeData) return null;

            const isExpanded = expandedScope === scope;
            const hasIssues = scopeData.expiringCount > 0;
            const hasDocuments = scopeData.totalCount > 0;
            const ScopeIcon = ICON_MAP[config.icon] || Shield;
            const currentStatus = expandedStatus[scope];

            // Skip empty scopes when searching
            if (searchQuery && scopeData.totalCount === 0) return null;

            return (
              <div
                key={scope}
                className={`bg-white rounded-lg shadow-md border overflow-hidden ${
                  hasIssues ? 'border-orange-200' : hasDocuments ? 'border-gray-200' : 'border-gray-100'
                }`}
              >
                {/* LEVEL 1: Scope Header */}
                <button
                  onClick={() => toggleScope(scope)}
                  className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${
                    isExpanded
                      ? `${config.bgColor} ${config.hoverColor}`
                      : hasDocuments
                        ? 'bg-gray-50 hover:bg-gray-100'
                        : 'bg-gray-25 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className={`w-5 h-5 ${config.textColor}`} />
                    ) : (
                      <ChevronRight className={`w-5 h-5 ${hasDocuments ? 'text-gray-500' : 'text-gray-300'}`} />
                    )}

                    {isExpanded ? (
                      <FolderOpen className={`w-5 h-5 ${config.textColor}`} />
                    ) : (
                      <Folder className={`w-5 h-5 ${hasDocuments ? 'text-amber-500' : 'text-gray-300'}`} />
                    )}

                    <ScopeIcon className={`w-5 h-5 ${config.textColor}`} />

                    <span className={`font-semibold ${
                      isExpanded ? `text-${config.color}-900` : hasDocuments ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {t(`documents.scopes.${scope}`, { defaultValue: config.label })}
                    </span>

                    {/* Entity count from DB */}
                    {scopeData.entityCount > 0 && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        isExpanded ? 'bg-amber-200 text-amber-800' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {t(`documents.entityCount.${scope}`, { count: scopeData.entityCount, defaultValue: `${scopeData.entityCount} ${config.labelSingular || config.label}` })}
                      </span>
                    )}

                    {/* Document count */}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      isExpanded ? config.badgeColor : hasDocuments ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {t('documents.list.docCount', { count: scopeData.totalCount })}
                    </span>

                    {/* Compliance summary for scope */}
                    {complianceReports.summaries[scope]?.totalEntities > 0 && (
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                        complianceReports.summaries[scope].averageCompletenessPercent === 100
                          ? 'bg-green-100 text-green-700'
                          : complianceReports.summaries[scope].averageCompletenessPercent >= 75
                            ? 'bg-yellow-100 text-yellow-700'
                            : complianceReports.summaries[scope].averageCompletenessPercent >= 50
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-red-100 text-red-700'
                      }`}>
                        <TrendingUp className="w-3 h-3" />
                        {t('documents.list.compliance', { percent: complianceReports.summaries[scope].averageCompletenessPercent })}
                      </span>
                    )}

                    {hasIssues && (
                      <span className="flex items-center gap-1 text-orange-600">
                        <FileWarning className="w-4 h-4" />
                        <span className="text-xs font-medium">
                          {t('documents.list.toRenew', { count: scopeData.expiringCount })}
                        </span>
                      </span>
                    )}
                  </div>

                  <span className={`text-sm ${hasDocuments ? 'text-gray-500' : 'text-gray-300'}`}>
                    {hasDocuments ? formatSize(scopeData.totalSize) : '-'}
                  </span>
                </button>

                {/* LEVEL 2: Status (ACTIFS / ARCHIVES) */}
                {isExpanded && (
                  <div className="p-4 space-y-3 bg-white">
                    {/* ACTIFS section */}
                    <div className={`border rounded-lg overflow-hidden ${
                      scopeData.active.totalCount > 0 ? 'border-green-200' : 'border-gray-100'
                    }`}>
                      <button
                        onClick={() => toggleStatus(scope, 'active')}
                        className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
                          currentStatus === 'active'
                            ? 'bg-green-50 hover:bg-green-100'
                            : scopeData.active.totalCount > 0
                              ? 'bg-gray-50 hover:bg-gray-100'
                              : 'bg-white hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {currentStatus === 'active' ? (
                            <ChevronDown className="w-4 h-4 text-green-600" />
                          ) : (
                            <ChevronRight className={`w-4 h-4 ${scopeData.active.totalCount > 0 ? 'text-gray-500' : 'text-gray-300'}`} />
                          )}
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="font-semibold text-green-800">{t('documents.list.active')}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            currentStatus === 'active' ? 'bg-green-200 text-green-800' : 'bg-green-100 text-green-700'
                          }`}>
                            {scopeData.active.totalCount}
                          </span>
                          {scopeData.active.warningCount > 0 && (
                            <span className="flex items-center gap-1 text-orange-600 text-xs">
                              <AlertTriangle className="w-3 h-3" />
                              {t('documents.list.toRenew', { count: scopeData.active.warningCount })}
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">
                          {formatSize(scopeData.active.totalSize)}
                        </span>
                      </button>

                      {/* LEVEL 3: Entities within ACTIFS */}
                      {currentStatus === 'active' && (
                        <div className="p-3 space-y-2 bg-green-25 border-t border-green-100">
                          {Object.keys(scopeData.active.entities).length === 0 ? (
                            <div className="text-center py-4 text-gray-400">
                              <File className="w-6 h-6 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">{t('documents.list.noActiveDoc')}</p>
                            </div>
                          ) : (
                            Object.entries(scopeData.active.entities).map(([entityId, entityData]) => {
                              const entityKey = `${scope}-active-${entityId}`;
                              const isEntityExpanded = expandedEntities.has(entityKey);
                              const entityInfo = entityMaps[scope]?.[entityId];
                              const EntityIcon = entityInfo?.icon || Folder;
                              // Get compliance report for this entity
                              const complianceReport = complianceReports.reports[scope]?.[entityId];
                              const completeness = complianceReport?.completenessPercent || 0;
                              const hasCriticalIssues = complianceReport?.hasCriticalIssues || false;

                              return (
                                <div key={entityKey} className={`border rounded-lg overflow-hidden bg-white ${
                                  hasCriticalIssues ? 'border-red-200' : 'border-gray-200'
                                }`}>
                                  {/* Entity header */}
                                  <button
                                    onClick={() => toggleEntity(entityKey)}
                                    className={`w-full px-4 py-2 flex items-center justify-between transition-colors ${
                                      isEntityExpanded ? 'bg-amber-50 hover:bg-amber-100' : 'bg-gray-50 hover:bg-gray-100'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      {isEntityExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-amber-600" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-500" />
                                      )}
                                      <EntityIcon className={`w-4 h-4 ${isEntityExpanded ? 'text-amber-600' : 'text-gray-500'}`} />
                                      <span className="font-medium text-sm text-gray-900">
                                        {entityInfo?.label || entityId}
                                      </span>
                                      {entityInfo?.sublabel && (
                                        <span className="text-xs text-gray-500">
                                          ({entityInfo.sublabel})
                                        </span>
                                      )}
                                      <code className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-xs rounded font-mono">
                                        {entityId}
                                      </code>
                                      {entityData.totalCount > 0 && (
                                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                          {t('documents.list.docCount', { count: entityData.totalCount })}
                                        </span>
                                      )}
                                      {entityData.totalCount === 0 && (
                                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400">
                                          {t('documents.list.empty')}
                                        </span>
                                      )}
                                      {/* Completeness badge */}
                                      {complianceReport && (
                                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                          completeness === 100 ? 'bg-green-100 text-green-700' :
                                          completeness >= 75 ? 'bg-yellow-100 text-yellow-700' :
                                          completeness >= 50 ? 'bg-orange-100 text-orange-700' :
                                          'bg-red-100 text-red-700'
                                        }`}>
                                          <TrendingUp className="w-3 h-3" />
                                          {completeness}%
                                        </span>
                                      )}
                                      {hasCriticalIssues && (
                                        <AlertTriangle className="w-4 h-4 text-red-500" />
                                      )}
                                      {!hasCriticalIssues && entityData.warningCount > 0 && (
                                        <Clock className="w-4 h-4 text-orange-500" />
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-500">
                                      {entityData.totalSize > 0 ? formatSize(entityData.totalSize) : '-'}
                                    </span>
                                  </button>

                                  {/* LEVEL 4: Folders within Entity - Show ALL folders from compliance report */}
                                  {isEntityExpanded && complianceReport && (
                                    <div className="p-2 space-y-2 bg-white border-t border-gray-100">
                                      {complianceReport.folders.map((folderReport) => {
                                        const folderKey = `${entityKey}-${folderReport.folderCode}`;
                                        const isFolderExpanded = expandedFolders.has(folderKey);
                                        const statusStyle = STATUS_STYLE_MAP[folderReport.status] || STATUS_STYLE_MAP[FOLDER_STATUS.OPTIONAL_EMPTY];
                                        const StatusIcon = statusStyle.icon;
                                        const hasDocuments = folderReport.documentCount > 0;

                                        return (
                                          <div key={folderKey} className={`border rounded-lg overflow-hidden ${statusStyle.borderColor}`}>
                                            {/* Folder header */}
                                            <button
                                              onClick={() => hasDocuments && toggleFolder(folderKey)}
                                              className={`w-full px-3 py-2 flex items-center justify-between transition-colors ${
                                                isFolderExpanded ? 'bg-gray-100' : `${statusStyle.bgColor} hover:bg-gray-100`
                                              } ${!hasDocuments ? 'cursor-default' : 'cursor-pointer'}`}
                                            >
                                              <div className="flex items-center gap-2">
                                                {hasDocuments ? (
                                                  isFolderExpanded ? (
                                                    <ChevronDown className="w-3 h-3 text-gray-500" />
                                                  ) : (
                                                    <ChevronRight className="w-3 h-3 text-gray-400" />
                                                  )
                                                ) : (
                                                  <span className="w-3 h-3" /> /* Spacer for alignment */
                                                )}
                                                {hasDocuments ? (
                                                  isFolderExpanded ? (
                                                    <FolderOpen className="w-4 h-4 text-blue-500" />
                                                  ) : (
                                                    <Folder className="w-4 h-4 text-amber-400" />
                                                  )
                                                ) : (
                                                  <Folder className="w-4 h-4 text-gray-300" />
                                                )}
                                                <span className={`text-xs ${hasDocuments ? 'text-gray-700' : 'text-gray-400'}`}>
                                                  {t(`documents.folders.${folderReport.folderCode}`, { defaultValue: folderReport.label })}
                                                </span>
                                                {/* Status badge */}
                                                <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${statusStyle.bgColor} ${statusStyle.textColor}`}>
                                                  <StatusIcon className="w-3 h-3" />
                                                  {folderReport.documentCount > 0
                                                    ? t('documents.list.statusWithCount', { count: folderReport.documentCount, status: t(`documents.folderStatus.${folderReport.status}`) })
                                                    : t(`documents.folderStatus.${folderReport.status}`)}
                                                </span>
                                                {/* Required indicator */}
                                                {folderReport.required && folderReport.conditionMet && (
                                                  <span className="px-1 py-0.5 text-xs text-gray-500 italic">{t('documents.list.required')}</span>
                                                )}
                                                {/* Expiry days indicator */}
                                                {folderReport.daysUntilExpiry !== null && folderReport.status === FOLDER_STATUS.EXPIRING_SOON && (
                                                  <span className="text-xs text-orange-600 font-medium">
                                                    {t('documents.list.daysLeft', { days: folderReport.daysUntilExpiry })}
                                                  </span>
                                                )}
                                                {/* Upload button */}
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleUploadClick(expandedScope, entityId, folderReport.folderCode);
                                                  }}
                                                  className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                                  title={t('documents.list.addDocument')}
                                                >
                                                  <Upload className="w-4 h-4" />
                                                </button>
                                              </div>
                                              <div className="flex items-center gap-2">
                                                {/* Validation button - show for pending validation or validated docs */}
                                                {hasDocuments && (
                                                  folderReport.status === FOLDER_STATUS.PENDING_VALIDATION ? (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleValidateClick(folderReport.folderCode, folderReport.documents);
                                                      }}
                                                      className="p-1 text-amber-600 hover:bg-amber-100 rounded"
                                                      title={t('documents.list.validateThisDocument')}
                                                    >
                                                      <ClipboardCheck className="w-4 h-4" />
                                                    </button>
                                                  ) : folderReport.status === FOLDER_STATUS.VALIDATION_REJECTED ? (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleValidateClick(folderReport.folderCode, folderReport.documents);
                                                      }}
                                                      className="p-1 text-red-600 hover:bg-red-100 rounded"
                                                      title={t('documents.list.rejectedSeeDetails')}
                                                    >
                                                      <Eye className="w-4 h-4" />
                                                    </button>
                                                  ) : folderReport.documents?.some(d => d.validation_status === 'validated') ? (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleValidateClick(folderReport.folderCode, folderReport.documents);
                                                      }}
                                                      className="p-1 text-green-600 hover:bg-green-100 rounded"
                                                      title={t('documents.list.viewValidated')}
                                                    >
                                                      <CheckCircle className="w-4 h-4" />
                                                    </button>
                                                  ) : null
                                                )}
                                                <span className="text-xs text-gray-400">
                                                  {hasDocuments && entityData.folders[folderReport.folderCode]?.totalSize
                                                    ? formatSize(entityData.folders[folderReport.folderCode].totalSize)
                                                    : ''}
                                                </span>
                                              </div>
                                            </button>

                                            {/* LEVEL 5: Documents */}
                                            {isFolderExpanded && hasDocuments && (
                                              <div className="p-3 space-y-2 bg-white border-t border-gray-50">
                                                {folderReport.documents.map(doc => (
                                                  <DocumentItem
                                                    key={doc.id}
                                                    doc={doc}
                                                    isArchived={false}
                                                    onView={handleView}
                                                    onDownload={handleDownload}
                                                    onDelete={handleDelete}
                                                    onValidate={handleValidate}
                                                    isDeleting={deletingId === doc.id}
                                                    typeLabel={getDocumentTypeLabel(doc.document_type)}
                                                  />
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {/* Fallback if no compliance report */}
                                  {isEntityExpanded && !complianceReport && (
                                    <div className="p-2 space-y-2 bg-white border-t border-gray-100">
                                      {Object.entries(entityData.folders).map(([folderCode, folderData]) => {
                                        const folderKey = `${entityKey}-${folderCode}`;
                                        const isFolderExpanded = expandedFolders.has(folderKey);
                                        const folderConfig = folderData.config;

                                        return (
                                          <div key={folderKey} className="border border-gray-100 rounded-lg overflow-hidden">
                                            {/* Folder header */}
                                            <button
                                              onClick={() => toggleFolder(folderKey)}
                                              className={`w-full px-3 py-2 flex items-center justify-between transition-colors ${
                                                isFolderExpanded ? 'bg-gray-100' : 'bg-gray-50 hover:bg-gray-100'
                                              }`}
                                            >
                                              <div className="flex items-center gap-2">
                                                {isFolderExpanded ? (
                                                  <ChevronDown className="w-3 h-3 text-gray-500" />
                                                ) : (
                                                  <ChevronRight className="w-3 h-3 text-gray-400" />
                                                )}
                                                {isFolderExpanded ? (
                                                  <FolderOpen className="w-4 h-4 text-blue-500" />
                                                ) : (
                                                  <Folder className="w-4 h-4 text-amber-400" />
                                                )}
                                                <span className="text-xs text-gray-700">{t(`documents.folders.${folderCode}`, { defaultValue: folderConfig.label })}</span>
                                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${folderConfig.color}`}>
                                                  {folderData.docs.length}
                                                </span>
                                              </div>
                                              <span className="text-xs text-gray-400">
                                                {formatSize(folderData.totalSize)}
                                              </span>
                                            </button>

                                            {/* LEVEL 5: Documents */}
                                            {isFolderExpanded && (
                                              <div className="p-3 space-y-2 bg-white border-t border-gray-50">
                                                {folderData.docs.map(doc => (
                                                  <DocumentItem
                                                    key={doc.id}
                                                    doc={doc}
                                                    isArchived={false}
                                                    onView={handleView}
                                                    onDownload={handleDownload}
                                                    onDelete={handleDelete}
                                                    onValidate={handleValidate}
                                                    isDeleting={deletingId === doc.id}
                                                    typeLabel={getDocumentTypeLabel(doc.document_type)}
                                                  />
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>

                    {/* ARCHIVES section */}
                    <div className={`border rounded-lg overflow-hidden ${
                      scopeData.archived.totalCount > 0 ? 'border-gray-300' : 'border-gray-100'
                    }`}>
                      <button
                        onClick={() => toggleStatus(scope, 'archived')}
                        className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
                          currentStatus === 'archived'
                            ? 'bg-gray-100 hover:bg-gray-150'
                            : scopeData.archived.totalCount > 0
                              ? 'bg-gray-50 hover:bg-gray-100'
                              : 'bg-white hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {currentStatus === 'archived' ? (
                            <ChevronDown className="w-4 h-4 text-gray-600" />
                          ) : (
                            <ChevronRight className={`w-4 h-4 ${scopeData.archived.totalCount > 0 ? 'text-gray-500' : 'text-gray-300'}`} />
                          )}
                          <Archive className="w-5 h-5 text-gray-400" />
                          <span className="font-semibold text-gray-600">{t('documents.list.archived')}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            currentStatus === 'archived' ? 'bg-gray-300 text-gray-700' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {scopeData.archived.totalCount}
                          </span>
                          <span className="text-xs text-gray-400 italic">
                            {t('documents.list.archivedHint')}
                          </span>
                        </div>
                        <span className="text-sm text-gray-400">
                          {formatSize(scopeData.archived.totalSize)}
                        </span>
                      </button>

                      {/* LEVEL 3: Entities within ARCHIVES */}
                      {currentStatus === 'archived' && (
                        <div className="p-3 space-y-2 bg-gray-50 border-t border-gray-200">
                          {Object.keys(scopeData.archived.entities).length === 0 ? (
                            <div className="text-center py-4 text-gray-400">
                              <ArchiveRestore className="w-6 h-6 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">{t('documents.list.noArchivedDoc')}</p>
                            </div>
                          ) : (
                            Object.entries(scopeData.archived.entities).map(([entityId, entityData]) => {
                              const entityKey = `${scope}-archived-${entityId}`;
                              const isEntityExpanded = expandedEntities.has(entityKey);
                              const entityInfo = entityMaps[scope]?.[entityId];
                              const EntityIcon = entityInfo?.icon || Folder;

                              return (
                                <div key={entityKey} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                                  {/* Entity header */}
                                  <button
                                    onClick={() => toggleEntity(entityKey)}
                                    className={`w-full px-4 py-2 flex items-center justify-between transition-colors ${
                                      isEntityExpanded ? 'bg-gray-100' : 'bg-gray-50 hover:bg-gray-100'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      {isEntityExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-gray-500" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-400" />
                                      )}
                                      <EntityIcon className="w-4 h-4 text-gray-400" />
                                      <span className="font-medium text-sm text-gray-600">
                                        {entityInfo?.label || entityId}
                                      </span>
                                      {entityInfo?.sublabel && (
                                        <span className="text-xs text-gray-400">
                                          ({entityInfo.sublabel})
                                        </span>
                                      )}
                                      <code className="px-1.5 py-0.5 bg-gray-100 text-gray-400 text-xs rounded font-mono">
                                        {entityId}
                                      </code>
                                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-500">
                                        {t('documents.list.archivedCount', { count: entityData.totalCount })}
                                      </span>
                                    </div>
                                    <span className="text-xs text-gray-400">
                                      {formatSize(entityData.totalSize)}
                                    </span>
                                  </button>

                                  {/* LEVEL 4: Folders within Entity */}
                                  {isEntityExpanded && (
                                    <div className="p-2 space-y-2 bg-gray-50 border-t border-gray-100">
                                      {Object.entries(entityData.folders).map(([folderCode, folderData]) => {
                                        const folderKey = `${entityKey}-${folderCode}`;
                                        const isFolderExpanded = expandedFolders.has(folderKey);
                                        const folderConfig = folderData.config;

                                        return (
                                          <div key={folderKey} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                                            {/* Folder header */}
                                            <button
                                              onClick={() => toggleFolder(folderKey)}
                                              className={`w-full px-3 py-2 flex items-center justify-between transition-colors ${
                                                isFolderExpanded ? 'bg-gray-100' : 'bg-gray-50 hover:bg-gray-100'
                                              }`}
                                            >
                                              <div className="flex items-center gap-2">
                                                {isFolderExpanded ? (
                                                  <ChevronDown className="w-3 h-3 text-gray-500" />
                                                ) : (
                                                  <ChevronRight className="w-3 h-3 text-gray-400" />
                                                )}
                                                {isFolderExpanded ? (
                                                  <FolderOpen className="w-4 h-4 text-gray-500" />
                                                ) : (
                                                  <Folder className="w-4 h-4 text-gray-400" />
                                                )}
                                                <span className="text-xs text-gray-500">{t(`documents.folders.${folderCode}`, { defaultValue: folderConfig.label })}</span>
                                                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                                                  {folderData.docs.length}
                                                </span>
                                              </div>
                                              <span className="text-xs text-gray-400">
                                                {formatSize(folderData.totalSize)}
                                              </span>
                                            </button>

                                            {/* LEVEL 5: Documents */}
                                            {isFolderExpanded && (
                                              <div className="p-3 space-y-2 bg-gray-50 border-t border-gray-100">
                                                {folderData.docs.map(doc => (
                                                  <DocumentItem
                                                    key={doc.id}
                                                    doc={doc}
                                                    isArchived={true}
                                                    onView={handleView}
                                                    onDownload={handleDownload}
                                                    onDelete={handleDelete}
                                                    onValidate={handleValidate}
                                                    isDeleting={deletingId === doc.id}
                                                    typeLabel={getDocumentTypeLabel(doc.document_type)}
                                                  />
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {!loading && groupedData.totalCount === 0 && !searchQuery && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <HardDrive className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('documents.empty.title')}</h3>
          <p className="text-gray-600 mb-4">{t('documents.empty.description')}</p>
          <button
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Upload className="w-4 h-4" />
            {t('documents.header.addDocuments')}
          </button>
        </div>
      )}

      {/* No search results */}
      {!loading && groupedData.totalCount === 0 && searchQuery && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('common.states.noResult')}</h3>
          <p className="text-gray-600">
            {t('documents.empty.noSearchResult', { query: searchQuery })}
          </p>
        </div>
      )}

      {/* Validation Modals */}
      <DocumentValidationModal
        isOpen={validationModal.isOpen}
        onClose={() => setValidationModal({ isOpen: false, document: null, entityData: {}, viewOnly: false })}
        doc={validationModal.document}
        entityData={validationModal.entityData}
        viewOnly={validationModal.viewOnly || false}
        onValidated={handleValidationCompleted}
        onRejectClick={(d) => {
          setValidationModal({ isOpen: false, document: null, entityData: {}, viewOnly: false });
          setRejectionModal({ isOpen: true, document: d });
        }}
        onCannotProvideClick={(d) => {
          setValidationModal({ isOpen: false, document: null, entityData: {}, viewOnly: false });
          setCannotProvideModal({ isOpen: true, document: d });
        }}
      />

      <DocumentRejectionModal
        isOpen={rejectionModal.isOpen}
        onClose={() => setRejectionModal({ isOpen: false, document: null })}
        doc={rejectionModal.document}
        onRejected={handleRejectionCompleted}
      />

      <CannotProvideModal
        isOpen={cannotProvideModal.isOpen}
        onClose={() => setCannotProvideModal({ isOpen: false, document: null })}
        doc={cannotProvideModal.document}
        onRecorded={handleCannotProvideCompleted}
      />

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={!!viewerDocument}
        doc={viewerDocument}
        onClose={() => setViewerDocument(null)}
        onDownload={(d) => handleDownload(d)}
      />

      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
        onChange={handleFileChange}
      />

      {/* Upload loading overlay */}
      {uploading && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            <span className="text-gray-700">{t('documents.messages.uploading')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentsView;
